import { alpha, useTheme } from '@mui/material';
import {
  autoFormat,
  quickOptimization,
  saveIgnoredSuggestions,
  saveIgnoredSuggestionsExternal,
  textAnalysis,
  textAnalysisExternal,
} from 'API/augmented-writing';
import { createUserBehavior, createUserBehaviorExternal, getLastSession, getLastSessionExternal } from 'API/user-behavior';
import { createUserTracker, createUserTrackerExternal } from 'API/user-tracker';
import config from 'config/default';
import { AWTriggers, AWTypes, SessionStatus, TooltipContentType } from 'enums';
import {
  AWSuggestionContent,
  AugmentedAnalyses,
  GrammarReplacement,
  HighlightTextKeys,
  HighlightTexts,
  HighlightType,
  Paraphrase,
  Score,
  Scores,
  SidebarNote,
  SuggestionItem,
  WordSuggestionsResponse,
} from 'features/augmented-writing/augmented-writing.types';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import _ from 'lodash';
import Logger from 'logger';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Quill } from 'react-quill';
import useErrorStore from 'stores/error-store';
import { Assessment } from 'types/analysis-result.types';
import { ProfileValues } from 'types/profile.types';
import { DetailsCategoryScore, ImitationScore } from 'types/user-tracker.types';

export const defaultScores = {
  diversity: -0.1,
  overall: 0,
  percentiles: {
    profile: {
      diversity: -0.1,
      jointPosAchieve: 0,
      jointPosPower: 0,
      jointPosAffil: 0,
      authenticity: 0,
      regFocus: 0,
      gender: 0,
    },
    text: {
      diversity: -0.1,
      jointPosAchieve: 0,
      jointPosPower: 0,
      jointPosAffil: 0,
      authenticity: 0,
      regFocus: 0,
      gender: 0,
    },
  },
};

export const useAugmentedWriting = (
  editor: any | null,
  domain: AWTypes,
  profile: ProfileValues | undefined,
  profileId: string | undefined,
  occupation?: string | undefined,
  apiKey?: string | undefined
) => {
  const { firstName, lastName } = useUserData();
  const { t } = useTranslation();
  const theme = useTheme();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const scoreRef = useRef<Scores>(defaultScores);
  const currentSessionRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<Date | undefined>(undefined);
  const startingOverallScoreRef = useRef<number | undefined>(undefined);
  const endingOverallScoreRef = useRef<number | undefined>(undefined);
  const startingCategoriesScoreRef = useRef<DetailsCategoryScore | undefined>(undefined);
  const endingCategoriesScoreRef = useRef<DetailsCategoryScore | undefined>(undefined);
  const profileIdRef = useRef<string | undefined>(undefined);
  const usernameRef = useRef<string | undefined>(`${firstName} ${lastName}`);
  const autoTriggerRef = useRef<boolean>(false);

  const [augmentedWritingResponse, setAugmentedWritingResponse] = useState<WordSuggestionsResponse>();
  const highlights: HighlightType[] = augmentedWritingResponse?.highlights ?? [];
  const sidebarNotes: SidebarNote[] = augmentedWritingResponse?.sidebarNotes ?? [];
  const [quickOptimizationScores, setQuickOptimizationScores] = useState<Scores>();
  const [quickOptimizationUserDone, setQuickOptimizationUserDone] = useState<boolean>();
  const scores: Scores | undefined = augmentedWritingResponse?.scores || quickOptimizationScores;
  const userAssessment: Assessment | undefined = augmentedWritingResponse?.assessment;
  const userDone: boolean | undefined = augmentedWritingResponse?.userDone || quickOptimizationUserDone;

  const highlightColors: AugmentedAnalyses['highlightColors'] | undefined = augmentedWritingResponse?.highlightColors;
  const highlightTexts: HighlightTexts | undefined = augmentedWritingResponse?.highlightTexts;
  const [dynamicStyles, setDynamicStyles] = useState<string>('');
  const [clickedChartCategory, setClickedChartCategory] = useState<string | string[]>();
  const [clickedChartCategoryKey, setClickedChartCategoryKey] = useState<string>();
  const [clickedSidebarNotesCategory, setClickedSidebarNotesCategory] = useState<string | string[]>();
  const [modifiedHighlightTexts, setModifiedHighlightTexts] = useState<HighlightTexts>();

  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [grammarReplacements, setGrammarReplacements] = useState<GrammarReplacement[]>([]);
  const [paraphrases, setParaphrases] = useState<Paraphrase[]>([]);
  const [allSuggestions, setAllSuggestions] = useState<SuggestionItem[]>([]);
  const [fingerprints, setFingerprints] = useState<string[]>([]);

  const [emptyEditor, setEmptyEditor] = useState(true);
  const [activeSuggestion, setActiveSuggestion] = useState<string | undefined>();

  const [successDialog, setSuccessDialog] = useState<{ open: boolean; content: string }>({ open: false, content: '' });

  const [optimizationLoading, setOptimizationLoading] = useState<boolean>(false);
  const [autoFormatLoading, setAutoFormatLoading] = useState<boolean>(false);
  const [optimizationMatch, setOptimizationMatch] = useState<number | null>(null);

  const languageOptions = i18n.languages[0] || localStorage.getItem('i18nextLng');
  const language = languageOptions!.split('-')[0];

  const createUserActionsAnalysis = async () => {
    const endingScore = endingOverallScoreRef.current ? endingOverallScoreRef.current : startingOverallScoreRef.current;
    if (startingOverallScoreRef.current && startTimeRef.current) {
      if (typeof currentSessionRef.current === 'number') {
        await createUserBehavior(currentSessionRef.current, SessionStatus.END, scoreRef.current, endingScore!);
      }
      const endingCategoryScore = endingCategoriesScoreRef.current ? endingCategoriesScoreRef.current : startingCategoriesScoreRef.current;
      const improvementScore = Number((endingScore! - startingOverallScoreRef.current).toFixed(3));
      const imitationOverallScore: ImitationScore = {
        start: startingOverallScoreRef.current,
        end: endingScore!,
      };
      const endTime = new Date();
      const secondsTaken = Math.ceil((endTime.getTime() - startTimeRef.current.getTime()) / 1000);
      await createUserTracker(
        profileIdRef.current,
        usernameRef.current,
        startTimeRef.current,
        endTime,
        imitationOverallScore,
        startingCategoriesScoreRef.current,
        endingCategoryScore,
        improvementScore,
        secondsTaken
      );
    }
    startTimeRef.current = new Date();
  };

  const createOutlookUserActionsAnalysis = async () => {
    if (startingOverallScoreRef.current && startTimeRef.current && apiKey) {
      const endingScore = endingOverallScoreRef.current ? endingOverallScoreRef.current : startingOverallScoreRef.current;
      const endingCategoryScore = endingCategoriesScoreRef.current ? endingCategoriesScoreRef.current : startingCategoriesScoreRef.current;
      const improvementScore = Number((endingScore - startingOverallScoreRef.current).toFixed(3));
      const imitationOverallScore = {
        start: startingOverallScoreRef.current,
        end: endingScore,
      };
      const endTime = new Date();
      const secondsTaken = Math.ceil((endTime.getTime() - startTimeRef.current.getTime()) / 1000);
      await createUserTrackerExternal(
        apiKey,
        profileIdRef.current,
        usernameRef.current,
        startTimeRef.current,
        endTime,
        imitationOverallScore,
        startingCategoriesScoreRef.current,
        endingCategoryScore,
        improvementScore,
        secondsTaken
      );
    }
    startTimeRef.current = new Date();
  };

  useEffect(() => {
    if (_.isNil(apiKey)) {
      window.addEventListener('beforeunload', createUserActionsAnalysis);
    }

    return () => {
      if (_.isNil(apiKey)) {
        window.removeEventListener('beforeunload', createUserActionsAnalysis);
      } else {
        createOutlookUserActionsAnalysis();
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!_.isNil(profileId)) {
      profileIdRef.current = profileId;
    }
  }, [profileId]);

  useEffect(() => {
    scoreRef.current = augmentedWritingResponse?.scores || defaultScores;
    if (augmentedWritingResponse?.suggestions) {
      if (augmentedWritingResponse?.suggestions.grammarReplacement) {
        setGrammarReplacements(augmentedWritingResponse?.suggestions.grammarReplacement);
      } else {
        setGrammarReplacements([]);
      }

      if (augmentedWritingResponse?.suggestions.paraphrases) {
        setParaphrases(augmentedWritingResponse?.suggestions.paraphrases);
      } else {
        setParaphrases([]);
      }
    }
  }, [augmentedWritingResponse?.suggestions]);

  useEffect(() => {
    const mappedGrammarReplacements: SuggestionItem[] =
      grammarReplacements?.map((item) => ({
        contentType: TooltipContentType.GRAMMAR_REPLACEMENT,
        content: item,
        index: item.ranking_index,
        highlightId: `${TooltipContentType.GRAMMAR_REPLACEMENT}-${item.ranking_index}`,
      })) || [];
    const mappedParaphrases: SuggestionItem[] =
      paraphrases?.map((item) => ({
        contentType: TooltipContentType.PARAPHRASE,
        content: item,
        index: item.ranking_index,
        highlightId: `${TooltipContentType.PARAPHRASE}-${item.ranking_index}`,
      })) || [];

    const concatArray: SuggestionItem[] = _.concat(mappedGrammarReplacements, mappedParaphrases);

    const sortedArray = _.sortBy(concatArray, (suggestion) => suggestion.index);

    if (!_.isNil(editor)) {
      const firstSuggestion = _.head(sortedArray);
      if (firstSuggestion) {
        try {
          const element = document.getElementById(firstSuggestion.highlightId);
          if (element) {
            const targetBlot = Quill.find(element) as any;
            const index = targetBlot?.offset(editor.scroll);
            if (index !== undefined) {
              editor.formatText(
                index,
                firstSuggestion.content?.highlightPosition[1] - firstSuggestion.content?.highlightPosition[0],
                {
                  background:
                    firstSuggestion.contentType !== TooltipContentType.GRAMMAR_REPLACEMENT
                      ? alpha(theme.palette.primary.main, 0.2)
                      : 'rgba(0, 0, 0, 0.1)',
                },
                'api'
              );
            }
          }
        } catch (e) {
          Logger.error('First Suggestion Error: ', e);
        }
      }
    }

    setAllSuggestions(sortedArray);
  }, [grammarReplacements, paraphrases]);

  useEffect(() => {
    if (!_.isEmpty(allSuggestions)) {
      setActiveSuggestion(_.head(allSuggestions)?.highlightId);
    }
  }, [allSuggestions]);

  function scrollToPosition(element: HTMLElement) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });
  }

  const updateHighlightBackground = useCallback(
    (firstSuggestion?: SuggestionItem, prevSuggestion?: SuggestionItem, stayHighlighted?: boolean) => {
      if (!_.isNil(editor)) {
        if (prevSuggestion) {
          try {
            const prevElement = document.getElementById(prevSuggestion.highlightId);
            if (prevElement) {
              const targetBlot = Quill.find(prevElement) as any;
              const index = targetBlot?.offset(editor.scroll);
              if (index !== undefined) {
                editor.formatText(
                  index,
                  prevSuggestion.content?.highlightPosition[1] - prevSuggestion.content?.highlightPosition[0],
                  stayHighlighted
                    ? { background: false }
                    : {
                        highlight: false,
                        background: false,
                      },
                  'api'
                );
              }
            }
          } catch (e) {
            Logger.error('Prev Suggestion Error: ', e);
          }
        }
        if (firstSuggestion) {
          try {
            const element = document.getElementById(firstSuggestion.highlightId);
            if (element) {
              const targetBlot = Quill.find(element) as any;
              const index = targetBlot?.offset(editor.scroll);
              if (index !== undefined) {
                scrollToPosition(element);
                editor.formatText(
                  index,
                  firstSuggestion.content?.highlightPosition[1] - firstSuggestion.content?.highlightPosition[0],
                  {
                    background:
                      firstSuggestion.contentType !== TooltipContentType.GRAMMAR_REPLACEMENT
                        ? alpha(theme.palette.primary.main, 0.2)
                        : 'rgba(0, 0, 0, 0.1)',
                  },
                  'api'
                );
              }
            }
          } catch (e) {
            Logger.error('First Suggestion Error: ', e);
          }
        }
      }
    },
    [allSuggestions]
  );

  function skipSuggestion(content: any, id: string) {
    return () => {
      const suggestions: AWSuggestionContent[] = content.suggestions;
      const newFingerprints: string[] = [];

      suggestions.forEach((suggestion) => {
        if (!_.isNil(suggestion.fingerprint)) {
          newFingerprints.push(suggestion.fingerprint);
        }
      });

      const currentFingerprints = [...fingerprints];
      const newIgnored = [...currentFingerprints, ...newFingerprints];

      setFingerprints(newIgnored);

      const updateAllSuggestions = _.cloneDeep(allSuggestions);
      const prevActiveSuggestion = updateAllSuggestions.find((suggestion) => suggestion.highlightId === id);
      _.pull(updateAllSuggestions, prevActiveSuggestion);
      const newActiveSuggestion = _.head(updateAllSuggestions);
      updateHighlightBackground(newActiveSuggestion, prevActiveSuggestion);
      setAllSuggestions(updateAllSuggestions);

      if (!_.isNil(newActiveSuggestion)) {
        setTimeout(() => {
          const element = document.getElementById(`card-${newActiveSuggestion.highlightId}`);
          if (!_.isNil(element)) {
            scrollToPosition(element);
          }
        }, 300);
      }
    };
  }

  function ignoreSuggestion(content: any, id: string) {
    return async () => {
      const suggestions: AWSuggestionContent[] = content.suggestions;
      const newFingerprints: string[] = [];

      suggestions.forEach((suggestion) => {
        if (!_.isNil(suggestion.fingerprint)) {
          newFingerprints.push(suggestion.fingerprint);
        }
      });

      const res = await saveIgnoredSuggestions(newFingerprints);

      if (res && !res.error) {
        openSuccessDialog(t('savedIgnoredSuggestionSuccess'))();
      } else {
        handleOpenErrorDialog(res?.error, '/misc/ignored-suggestions', 'post');
      }

      const updateAllSuggestions = _.cloneDeep(allSuggestions);
      const prevActiveSuggestion = updateAllSuggestions.find((suggestion) => suggestion.highlightId === id);
      _.pull(updateAllSuggestions, prevActiveSuggestion);
      const newActiveSuggestion = _.head(updateAllSuggestions);
      updateHighlightBackground(newActiveSuggestion, prevActiveSuggestion);
      setAllSuggestions(updateAllSuggestions);

      if (!_.isNil(newActiveSuggestion)) {
        setTimeout(() => {
          const element = document.getElementById(`card-${newActiveSuggestion.highlightId}`);
          if (!_.isNil(element)) {
            scrollToPosition(element);
          }
        }, 300);
      }
    };
  }

  function ignoreOutlookSuggestion(content: any, id: string) {
    return async () => {
      if (!_.isNil(apiKey)) {
        const suggestions: AWSuggestionContent[] = content.suggestions;
        const newFingerprints: string[] = [];

        suggestions.forEach((suggestion) => {
          if (!_.isNil(suggestion.fingerprint)) {
            newFingerprints.push(suggestion.fingerprint);
          }
        });

        const res = await saveIgnoredSuggestionsExternal(apiKey, newFingerprints);

        if (res && !res.error) {
          openSuccessDialog(t('savedIgnoredSuggestionSuccess'))();
        } else {
          // open fail dialog
        }

        const updateAllSuggestions = _.cloneDeep(allSuggestions);
        const prevActiveSuggestion = updateAllSuggestions.find((suggestion) => suggestion.highlightId === id);
        _.pull(updateAllSuggestions, prevActiveSuggestion);
        const newActiveSuggestion = _.head(updateAllSuggestions);
        updateHighlightBackground(newActiveSuggestion, prevActiveSuggestion);
        setAllSuggestions(updateAllSuggestions);

        if (!_.isNil(newActiveSuggestion)) {
          setTimeout(() => {
            const element = document.getElementById(`card-${newActiveSuggestion.highlightId}`);
            if (!_.isNil(element)) {
              scrollToPosition(element);
            }
          }, 300);
        }
      }
    };
  }

  function insertAtPosition(word: string, pos: number | number[]) {
    return () => {
      try {
        setTimeout(() => {
          if (!_.isNil(editor)) {
            const currentActiveSuggestion = allSuggestions.find((suggestion) => suggestion.highlightId === activeSuggestion);

            if (currentActiveSuggestion) {
              const activeElement = document.getElementById(currentActiveSuggestion.highlightId);
              if (activeElement) {
                const targetBlot = Quill.find(activeElement) as any;
                const index = targetBlot?.offset(editor.scroll);
                if (index !== undefined) {
                  if (word !== '') {
                    editor.deleteText(index, (pos as number[])[1] - (pos as number[])[0]);
                    editor.insertText(index, word);
                  } else {
                    editor.deleteText(index, (pos as number[])[1] - (pos as number[])[0] + 1);
                  }

                  if (_.isNil(apiKey)) {
                    analyzeText(AWTriggers.Suggestion)();
                  } else {
                    analyzeOutlookText(AWTriggers.Suggestion)();
                  }
                }
              }
            }
          }
        }, 100);
      } catch (e) {
        Logger.error('Insert Text Error', e);
      }
    };
  }

  function removeAllHighlights() {
    try {
      if (!_.isNil(editor)) {
        editor.formatText(
          0,
          editor.getLength(),
          {
            highlight: false,
            background: false,
          },
          'api'
        );
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  function updateSuggestionsFromEditor(idList: string[]) {
    const updateAllSuggestions: SuggestionItem[] = _.cloneDeep(allSuggestions);
    const filtered = updateAllSuggestions.filter((suggestion) => idList.includes(suggestion.highlightId));
    if (updateAllSuggestions.length !== filtered.length) {
      const prevSuggestion = _.head(updateAllSuggestions);
      const firstSuggestion = _.head(filtered);
      if (firstSuggestion !== prevSuggestion) {
        updateHighlightBackground(firstSuggestion, prevSuggestion);
      }
      setAllSuggestions(_.sortBy(filtered, (item) => item.index));
    }
  }

  function selectActiveSuggestion(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (target.tagName === 'SPAN' && !analysisLoading) {
      const id = target.id;

      const currentActiveSuggestion = allSuggestions.find((suggestion) => suggestion.highlightId === activeSuggestion);
      const newActiveSuggestion = allSuggestions.find((suggestion) => suggestion.highlightId === id);

      if (!_.isNil(newActiveSuggestion)) {
        if (newActiveSuggestion !== currentActiveSuggestion) {
          updateHighlightBackground(newActiveSuggestion, currentActiveSuggestion, true);
        }

        setActiveSuggestion(id);

        setTimeout(() => {
          const element = document.getElementById(`card-${newActiveSuggestion.highlightId}`);
          if (!_.isNil(element)) {
            scrollToPosition(element);
          }
        }, 300);
      }
    }
  }

  const setStyleSheet = (colorMapping?: Partial<AugmentedAnalyses['highlightColors']>) => {
    if (colorMapping) {
      const styling = _.reduce(
        colorMapping,
        (acc, color, category) => {
          return `${acc} .${category} {border-bottom: 2px solid ${theme.palette.primary.main}; background-color: ${alpha(
            theme.palette.primary.main,
            0.2
          )}}`;
        },
        ''
      );
      setDynamicStyles(styling);
    }
  };

  const setHighlightsCategoryColor = useCallback(
    (category: keyof AugmentedAnalyses['highlightColors'] | (keyof AugmentedAnalyses['highlightColors'])[]) => {
      const selectedCategoriesArray = _.isArray(category) ? category : [category];
      const selectedColors = _.pick(highlightColors, selectedCategoriesArray);
      const secondaryColors = _.mapKeys(selectedColors, (value, key) => {
        return `secondary-${key}`;
      });
      setStyleSheet({ ...selectedColors, ...secondaryColors });
    },
    [highlightColors]
  );

  const onChartCategoryClicked = useCallback(
    (category: any) => {
      if (!clickedSidebarNotesCategory) {
        let combinedCategory: any = '';
        const categoryDefinition: { composed: Score[] } = _.get(config, `augmentedWriting.customer.scores.${category}`) || { composed: [] };
        if (categoryDefinition) {
          // If composed is false or not set we use the key as the attribute name
          if (!categoryDefinition.composed) {
            combinedCategory = category;
            // Check if composed is an array of strings
          } else if (_.isArray(categoryDefinition.composed) && _.every(categoryDefinition.composed, _.isString)) {
            combinedCategory = categoryDefinition.composed;
          }
        } else {
          combinedCategory = category;
        }
        if (clickedChartCategory === combinedCategory || _.isEqual(clickedChartCategory, combinedCategory)) {
          setHighlightsCategoryColor(['none']);
          setClickedChartCategory(undefined);
          setClickedChartCategoryKey(undefined);
        } else {
          setClickedChartCategory(combinedCategory);
          setHighlightsCategoryColor(combinedCategory);
          setClickedChartCategoryKey(category);
        }
      }
    },
    [augmentedWritingResponse, highlightColors, clickedChartCategory, clickedSidebarNotesCategory]
  );

  function modifyHighlightTexts(text: string, removeCategories: HighlightTextKeys[]) {
    const tempHighlightTexts = _.cloneDeep(highlightTexts);
    _.forEach(removeCategories, (category) => {
      if (tempHighlightTexts) {
        tempHighlightTexts[category] = text;
      }
    });
    return tempHighlightTexts;
  }

  const onSidebarNotesCategoryClicked = useCallback(
    (category: any, sidebarNote: SidebarNote, manual?: boolean) => {
      setClickedChartCategory(undefined);
      setClickedChartCategoryKey(undefined);
      let combinedCategory: any = '';
      const categoryDefinition: { composed: Score[] } = _.get(config, `augmentedWriting.customer.scores.${category}`) || { composed: [] };
      if (categoryDefinition) {
        // If composed is false or not set we use the key as the attribute name
        if (!categoryDefinition.composed) {
          combinedCategory = category;
          // Check if composed is an array of strings
        } else if (_.isArray(categoryDefinition.composed) && _.every(categoryDefinition.composed, _.isString)) {
          combinedCategory = sidebarNote.remove_categories.length > 0 ? sidebarNote.remove_categories : categoryDefinition.composed;
        }
      } else {
        combinedCategory = category;
      }
      if ((clickedSidebarNotesCategory === combinedCategory || _.isEqual(clickedSidebarNotesCategory, combinedCategory) || !category) && !manual) {
        setHighlightsCategoryColor(['none']);
        setClickedSidebarNotesCategory(undefined);
        setModifiedHighlightTexts(undefined);
      } else {
        setClickedSidebarNotesCategory(combinedCategory);
        setHighlightsCategoryColor(combinedCategory);
        if (sidebarNote.remove_categories.length > 0 && sidebarNote.remove_text) {
          setModifiedHighlightTexts(modifyHighlightTexts(sidebarNote.remove_text, sidebarNote.remove_categories));
        }
      }
    },
    [augmentedWritingResponse, highlightColors, clickedSidebarNotesCategory, scores]
  );

  function checkIfEmpty(editorText: string) {
    if (editorText === '') {
      setEmptyEditor(true);
    } else {
      setEmptyEditor(false);
    }
  }

  function analyzeText(awTrigger: AWTriggers, isProfiling?: boolean, extraParaphrases: boolean = autoTriggerRef.current) {
    return async () => {
      if (!_.isNil(profile) && !_.isNil(editor)) {
        const editorText = editor?.getText() || '';
        removeAllHighlights();
        setAnalysisLoading(true);

        if (!startTimeRef.current) {
          startTimeRef.current = new Date();
        }

        const response = await textAnalysis(
          profile,
          language,
          editorText,
          domain,
          awTrigger,
          isProfiling,
          extraParaphrases,
          occupation,
          fingerprints
        );

        if (!_.isNil(response) && !response.error) {
          setAugmentedWritingResponse(response);
          setQuickOptimizationScores(undefined);
          setQuickOptimizationUserDone(undefined);

          const userScores = {
            authenticity: response.scores.percentiles.text.authenticity,
            diversity: response.scores.diversity,
            jointPosAchieve: response.scores.percentiles.text.jointPosAchieve,
            jointPosAffil: response.scores.percentiles.text.jointPosAffil,
            jointPosPower: response.scores.percentiles.text.jointPosPower,
            regFocus: response.scores.percentiles.text.regFocus,
          };

          if (!currentSessionRef.current) {
            getLastSession().then((sessionRes) => {
              const newSession = sessionRes.session;
              createUserBehavior(newSession, SessionStatus.START, userScores, response.scores.overall, domain, response.wordCount, awTrigger);
              currentSessionRef.current = newSession;
            });
          } else {
            createUserBehavior(
              currentSessionRef.current,
              SessionStatus.MIDDLE,
              userScores,
              response.scores.overall,
              domain,
              response.wordCount,
              awTrigger
            );
          }

          if (startingOverallScoreRef.current) {
            endingOverallScoreRef.current = response.scores.overall;
          } else {
            startingOverallScoreRef.current = response.scores.overall;
          }
          if (startingCategoriesScoreRef.current) {
            endingCategoriesScoreRef.current = userScores;
          } else {
            startingCategoriesScoreRef.current = userScores;
          }
        } else {
          handleOpenErrorDialog(response?.error, '/augmented-writing/suggestions', 'post');
        }
        setAnalysisLoading(false);
      }
    };
  }

  async function analyzeTextQuick() {
    setOptimizationLoading(true);

    if (!_.isNil(profile) && !_.isNil(editor)) {
      const editorText = editor.getText();

      let count = 1;
      let text = editorText;
      let error = false;

      const maxCount = 10;
      const maxScore = 0.99;

      if (!_.isNil(augmentedWritingResponse)) {
        resetAnalysis();
      }

      while (count <= maxCount && !error) {
        const response = await quickOptimization(profile, language, text, domain);

        if (!_.isNil(response) && !response.error) {
          if (!_.isNil(response.text) && response.text !== '') {
            editor.setText(response.text);
            setOptimizationMatch(response.match || null);
            setQuickOptimizationScores(response.scores);

            const score = response.match;
            text = response.text;
            count++;

            if (score >= maxScore || count > maxCount) {
              autoFormatText(response.text, response.user_done);
              break;
            }
          }
        } else {
          error = true;
          handleOpenErrorDialog(response?.error, '/augmented-writing/quick-optimize', 'post');
          break;
        }
      }
    }

    setOptimizationLoading(false);
  }

  async function autoFormatText(newText: string, done?: boolean) {
    setAutoFormatLoading(true);

    const res = await autoFormat(newText);

    if (!_.isNil(res) && !res.error) {
      const resText = res.text;

      if (!_.isNil(resText)) {
        editor.setText(resText);

        if (!done) {
          analyzeText(AWTriggers.Manual)();
        } else {
          setQuickOptimizationUserDone(true);
        }
      }
    } else {
      handleOpenErrorDialog(res?.error, '/misc/format-text', 'post');
    }
    setAutoFormatLoading(false);
  }

  function analyzeOutlookText(awTrigger: AWTriggers) {
    return async () => {
      if (!_.isNil(profile) && !_.isNil(editor) && !_.isNil(apiKey)) {
        const editorText = editor?.getText() || '';
        removeAllHighlights();
        setAnalysisLoading(true);

        if (!startTimeRef.current) {
          startTimeRef.current = new Date();
        }

        const response = await textAnalysisExternal(apiKey, editorText, profile, domain, awTrigger, fingerprints);

        if (!_.isNil(response) && !response.error) {
          setAugmentedWritingResponse(response);
          setQuickOptimizationScores(undefined);
          setQuickOptimizationUserDone(undefined);

          const userScores = {
            authenticity: response.scores.percentiles.text.authenticity,
            diversity: response.scores.diversity,
            jointPosAchieve: response.scores.percentiles.text.jointPosAchieve,
            jointPosAffil: response.scores.percentiles.text.jointPosAffil,
            jointPosPower: response.scores.percentiles.text.jointPosPower,
            regFocus: response.scores.percentiles.text.regFocus,
          };

          if (!currentSessionRef.current) {
            getLastSessionExternal(apiKey).then((sessionRes) => {
              const newSession = sessionRes.session;
              createUserBehaviorExternal(apiKey, newSession, SessionStatus.START, userScores, response.scores.overall, response.wordCount, awTrigger);
              currentSessionRef.current = newSession;
            });
          } else {
            createUserBehaviorExternal(
              apiKey,
              currentSessionRef.current,
              SessionStatus.MIDDLE,
              userScores,
              response.scores.overall,
              response.wordCount,
              awTrigger
            );
          }

          if (startingOverallScoreRef.current) {
            endingOverallScoreRef.current = response.scores.overall;
          } else {
            startingOverallScoreRef.current = response.scores.overall;
          }
          if (startingCategoriesScoreRef.current) {
            endingCategoriesScoreRef.current = userScores;
          } else {
            startingCategoriesScoreRef.current = userScores;
          }
        }
        setAnalysisLoading(false);
      }
    };
  }

  const activateSuggestionCard = (highlightId: string) => {
    return () => {
      const currentActiveSuggestion = allSuggestions.find((suggestion) => suggestion.highlightId === activeSuggestion);
      const newActiveSuggestion = allSuggestions.find((suggestion) => suggestion.highlightId === highlightId);

      if (!_.isNil(newActiveSuggestion)) {
        if (newActiveSuggestion !== currentActiveSuggestion) {
          updateHighlightBackground(newActiveSuggestion, currentActiveSuggestion, true);
        }
        setActiveSuggestion(highlightId);
      }
    };
  };

  const isSuggestionActive = (highlightId: string) => {
    return !_.isNil(activeSuggestion) && highlightId === activeSuggestion;
  };

  function openSuccessDialog(content: string) {
    return () => {
      setSuccessDialog({ open: true, content });
    };
  }

  const closeSuccessDialog = () => {
    setSuccessDialog({ ...successDialog, open: false });
  };

  const resetEditorHighlights = () => {
    setHighlightsCategoryColor(['none']);
    setClickedSidebarNotesCategory(undefined);
    setClickedChartCategory(undefined);
    setClickedChartCategoryKey(undefined);
    setModifiedHighlightTexts(undefined);
    setDynamicStyles('');
  };

  const resetAnalysis = () => {
    setAugmentedWritingResponse(undefined);
    setQuickOptimizationScores(undefined);
    setQuickOptimizationUserDone(undefined);
    setGrammarReplacements([]);
    setParaphrases([]);
    setActiveSuggestion(undefined);

    resetEditorHighlights();

    if (!_.isNil(editor)) {
      editor.formatText(
        0,
        editor.getLength(),
        {
          highlight: false,
          background: false,
        },
        'api'
      );
    }
  };

  return {
    scores,
    userAssessment,
    highlights,
    sidebarNotes,
    grammarReplacements,
    paraphrases,
    allSuggestions,
    skipSuggestion,
    ignoreSuggestion,
    ignoreOutlookSuggestion,
    analysisLoading,
    analyzeText,
    analyzeOutlookText,
    insertAtPosition,
    updateSuggestionsFromEditor,
    selectActiveSuggestion,
    dynamicStyles,
    clickedChartCategoryKey,
    clickedChartCategory,
    clickedSidebarNotesCategory,
    onChartCategoryClicked,
    onSidebarNotesCategoryClicked,
    highlightTexts,
    modifiedHighlightTexts,
    userDone,
    checkIfEmpty,
    emptyEditor,
    activateSuggestionCard,
    isSuggestionActive,
    successDialog,
    closeSuccessDialog,
    resetEditorHighlights,
    resetAnalysis,
    analyzeTextQuick,
    optimizationMatch,
    optimizationLoading,
    autoFormatLoading,
  };
};
